import React, { useState } from 'react';
import { CheckCircle2Icon, ClockIcon, FileTextIcon, PlusIcon, AlertTriangleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { DashboardScreen } from './DashboardScreen';
interface PTPListItem {
  id: string;
  task: string;
  location: string;
  contractor: string;
  date: string;
  status: 'complete' | 'in-progress' | 'draft';
  score?: number;
  crewSize: number;
}
const PTP_LIST: PTPListItem[] = [{
  id: 'PTP-2026-00847',
  task: 'Steel Beam Installation Using Mobile Crane',
  location: 'Level 2 Structural Frame',
  contractor: 'Apex Steel Works',
  date: 'June 12, 2026',
  status: 'complete',
  score: 72,
  crewSize: 5
}, {
  id: 'PTP-2026-00846',
  task: 'Concrete Pour — East Wing Foundation',
  location: 'Ground Level East',
  contractor: 'Summit Concrete Ltd.',
  date: 'June 11, 2026',
  status: 'complete',
  score: 91,
  crewSize: 8
}, {
  id: 'PTP-2026-00845',
  task: 'Scaffolding Erection — North Facade',
  location: 'North Exterior',
  contractor: 'Vertex Scaffolding',
  date: 'June 10, 2026',
  status: 'complete',
  score: 85,
  crewSize: 4
}, {
  id: 'PTP-2026-00844',
  task: 'Electrical Conduit Routing — Level 1',
  location: 'Level 1 Interior',
  contractor: 'BrightLine Electrical',
  date: 'June 9, 2026',
  status: 'complete',
  score: 94,
  crewSize: 3
}];
const statusConfig = {
  complete: {
    label: 'Complete',
    icon: CheckCircle2Icon,
    iconColor: 'text-green-500',
    bg: 'bg-green-500/10',
    text: 'text-green-400'
  },
  'in-progress': {
    label: 'In Progress',
    icon: ClockIcon,
    iconColor: 'text-amber-500',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400'
  },
  draft: {
    label: 'Draft',
    icon: FileTextIcon,
    iconColor: 'text-slate-500',
    bg: 'bg-slate-500/10',
    text: 'text-slate-400'
  }
};
export function Screen21_PTPList({
  data,
  onNext,
  showNewPTP = false
}: ScreenProps & {
  showNewPTP?: boolean;
}) {
  const [activeTab, setActiveTab] = useState<'plans' | 'dashboard'>('plans');
  const visiblePTPs = showNewPTP ? PTP_LIST : PTP_LIST.slice(1);
  return <div className="flex flex-col h-full" data-id="element-976">
      <div className="mb-4" data-id="element-977">
        <div className="flex items-center justify-between mb-1" data-id="element-978">
          <h2 className="text-2xl font-bold text-white" data-id="element-979">Pre-Task Plans</h2>
          <div data-id="element-980">
            <span className="text-white text-sm font-bold tracking-tight" data-id="element-981">
              Bilt
            </span>
            <span className="text-yellow-500 text-sm font-bold tracking-tight" data-id="element-982">
              On
            </span>
          </div>
        </div>
        <p className="text-slate-400 text-sm" data-id="element-983">Downtown Office Tower</p>
      </div>

      <div className="bg-slate-900 p-1 rounded-lg flex mb-5 flex-shrink-0" data-id="element-984">
        <button onClick={() => setActiveTab('plans')} className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition-colors ${activeTab === 'plans' ? 'bg-yellow-500 text-slate-950 shadow-sm' : 'text-slate-400'}`} data-id="element-985">
          Plans
        </button>
        <button onClick={() => setActiveTab('dashboard')} className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition-colors ${activeTab === 'dashboard' ? 'bg-yellow-500 text-slate-950 shadow-sm' : 'text-slate-400'}`} data-id="element-986">
          Dashboard
        </button>
      </div>

      {activeTab === 'dashboard' ? <DashboardScreen data-id="element-987" /> : <>
          <div className="flex gap-2 mb-5" data-id="element-988">
            <div className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-center" data-id="element-989">
              <p className="text-xl font-bold text-white" data-id="element-990">
                {visiblePTPs.length}
              </p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-991">
                Total PTPs
              </p>
            </div>
            <div className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-center" data-id="element-992">
              <p className="text-xl font-bold text-green-400" data-id="element-993">
                {visiblePTPs.filter(p => p.status === 'complete').length}
              </p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-994">
                Complete
              </p>
            </div>
            <div className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-center" data-id="element-995">
              <p className="text-xl font-bold text-white" data-id="element-996">
                {Math.round(visiblePTPs.reduce((sum, p) => sum + (p.score || 0), 0) / visiblePTPs.length)}
              </p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-997">
                Avg Score
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-2.5" data-id="element-998">
            {visiblePTPs.map((ptp, i) => {
          const config = statusConfig[ptp.status];
          const StatusIcon = config.icon;
          const isFirst = i === 0;
          return <motion.div key={ptp.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.08,
            duration: 0.3
          }} className={`bg-slate-900 border rounded-xl p-4 ${isFirst ? 'border-yellow-500/30 ring-1 ring-yellow-500/10' : 'border-slate-800'}`} data-id="element-999">
                  <div className="flex items-start justify-between gap-3 mb-2" data-id="element-1000">
                    <div className="flex-1 min-w-0" data-id="element-1001">
                      <p className="text-white font-semibold text-sm leading-snug" data-id="element-1002">
                        {ptp.task}
                      </p>
                      <p className="text-slate-500 text-xs mt-0.5" data-id="element-1003">
                        {ptp.location}
                      </p>
                      <p className="text-slate-400 text-xs mt-0.5" data-id="element-1004">
                        {ptp.contractor}
                      </p>
                    </div>
                    <div className={`${config.bg} px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0`} data-id="element-1005">
                      <StatusIcon className={`w-3 h-3 ${config.iconColor}`} data-id="element-1006" />
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${config.text}`} data-id="element-1007">
                        {config.label}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs" data-id="element-1008">
                    <span className="text-slate-500" data-id="element-1009">{ptp.date}</span>
                    <span className="text-slate-700" data-id="element-1010">·</span>
                    <span className="text-slate-500" data-id="element-1011">
                      {ptp.crewSize} workers
                    </span>
                    <span className="text-slate-700" data-id="element-1012">·</span>
                    {ptp.score && <span className="flex items-center gap-1" data-id="element-1013">
                        {ptp.score < 80 && <AlertTriangleIcon className="w-3 h-3 text-amber-500" data-id="element-1014" />}
                        <span className={ptp.score >= 80 ? 'text-green-400 font-medium' : 'text-amber-400 font-medium'} data-id="element-1015">
                          {ptp.score}/100
                        </span>
                      </span>}
                  </div>
                  {isFirst && <div className="mt-2 pt-2 border-t border-slate-800" data-id="element-1016">
                      <span className="text-[10px] text-yellow-400 font-bold uppercase tracking-wider" data-id="element-1017">
                        Just completed
                      </span>
                    </div>}
                  <div className="text-[10px] text-slate-600 mt-1" data-id="element-1018">
                    {ptp.id}
                  </div>
                </motion.div>;
        })}
          </div>

          <div className="pt-4 bg-slate-950" data-id="element-1019">
            <PrimaryButton onClick={onNext} icon={<PlusIcon className="w-5 h-5" data-id="element-1021" />} data-id="element-1020">
              New Pre-Task Plan
            </PrimaryButton>
          </div>
        </>}
    </div>;
}